// src/components/ClientPortalCases.js

import React, { useState, useCallback } from 'react';

function ClientPortalCases({ currentUser, clients, cases }) {
  // Find the specific client profile linked to the current user
  const clientProfile = clients.find(client => client.user === currentUser.id);

  // Filter cases relevant to this specific client profile
  const clientCases = cases.filter(c => c.client === (clientProfile ? clientProfile.id : null));

  const [showSubCases, setShowSubCases] = useState({}); // State to manage visibility of sub-cases

  // Function to get sub-cases for a given parent case
  const getSubCases = useCallback((parentId) => {
    return clientCases.filter(c => c.parent_case === parentId);
  }, [clientCases]);

  // Toggle sub-case visibility
  const toggleSubCases = useCallback((caseId) => {
    setShowSubCases(prev => ({
      ...prev,
      [caseId]: !prev[caseId]
    }));
  }, []);

  return (
    <div>
      <h2 className="text-2xl font-bold text-primary mb-1">Your Cases</h2>
      {clientProfile ? (
        <p className="text-gray-500 mb-6">View all cases associated with your profile, {clientProfile.first_name} {clientProfile.last_name}.</p>
      ) : (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <p className="text-yellow-700">Your user account is not yet linked to a client profile. Please contact the firm administrator to view your cases.</p>
        </div>
      )}

      {clientCases.length > 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Phase</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Opening Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Assigned Attorney</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Court Info</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {clientCases.filter(c => !c.parent_case).map(_case => (
                  <React.Fragment key={_case.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-800">
                        {_case.title}
                        {getSubCases(_case.id).length > 0 && (
                          <button
                            className="ml-2 text-accent hover:text-blue-800"
                            onClick={() => toggleSubCases(_case.id)}
                            title={showSubCases[_case.id] ? 'Hide Sub-Cases' : 'Show Sub-Cases'}
                          >
                            <i className={`bi ${showSubCases[_case.id] ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{_case.case_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{_case.status}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{_case.phase}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{_case.opening_date}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{_case.assigned_attorney_username || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {_case.court_name && <div><strong>Court:</strong> {_case.court_name}</div>}
                        {_case.jurisdiction && <div><strong>Jurisdiction:</strong> {_case.jurisdiction}</div>}
                        {_case.court_case_number && <div><strong>Case #:</strong> {_case.court_case_number}</div>}
                        {!_case.court_name && !_case.jurisdiction && !_case.court_case_number && 'N/A'}
                      </td>
                    </tr>
                    {showSubCases[_case.id] && getSubCases(_case.id).map(subCase => (
                      <tr key={subCase.id} className="bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700 pl-8">
                          <i className="bi bi-arrow-return-right mr-2 text-gray-400"></i> {subCase.title}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{subCase.case_type}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{subCase.status}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{subCase.phase}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{subCase.opening_date}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{subCase.assigned_attorney_username || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {subCase.court_name && <div><strong>Court:</strong> {subCase.court_name}</div>}
                          {subCase.jurisdiction && <div><strong>Jurisdiction:</strong> {subCase.jurisdiction}</div>}
                          {subCase.court_case_number && <div><strong>Case #:</strong> {subCase.court_case_number}</div>}
                          {!subCase.court_name && !subCase.jurisdiction && !subCase.court_case_number && 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-700">No cases found associated with your client profile.</p>
        </div>
      )}
    </div>
  );
}

export default ClientPortalCases;
